import { type KeyboardEvent, useEffect, useRef } from 'react'

import { useMutation, useQueryClient } from 'react-query'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { VendorTypes } from '@webapp/modules/vendor/entities/enums/VendorTypeEnum'
import { type CreditCardProviderOption } from '@webapp/platform/banking'
import { CREDIT_CARD_PROVIDER_OPTIONS_QUERY_KEY } from '@webapp/platform/banking'
import messages from './messages'
import { vendorRepository } from '../../data-access/repositories/VendorRepository'

export const useCreditCardProviderInlineCreate = ({
  name,
  onCreated,
}: {
  name: string
  onCreated: (provider: CreditCardProviderOption) => void
}) => {
  const intl = useProcurifyIntl()
  const queryClient = useQueryClient()

  const nameInputRef = useRef<HTMLInputElement>(null)

  const {
    mutateAsync,
    isLoading: isSaving,
    isError: hasCreateFailed,
  } = useMutation({
    mutationFn: (providerName: string) =>
      vendorRepository.createVendor({
        name: providerName,
        email: [],
        type: VendorTypes.CREDIT_CARD_PROVIDER,
      }),
  })

  // The name field is disabled while saving, which drops its focus. Once a
  // failed create re-enables it, put the user back in it to retry.
  useEffect(() => {
    if (hasCreateFailed) nameInputRef.current?.focus()
  }, [hasCreateFailed])

  // Inline provider creation (BHV-018): POST the new type-6 vendor and append
  // it to the select's cached options before handing it to the consumer.
  const createProvider = async () => {
    const providerName = name.trim()
    if (!providerName) return

    try {
      const result = await mutateAsync(providerName)
      const provider: CreditCardProviderOption = {
        id: result.data.id,
        name: result.data.name,
      }

      queryClient.setQueryData<CreditCardProviderOption[]>(
        CREDIT_CARD_PROVIDER_OPTIONS_QUERY_KEY,
        (options) => [...(options ?? []), provider]
      )

      onCreated(provider)
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.createErrorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.createErrorToastMessage),
      })
    }
  }

  return {
    refs: {
      nameInputRef,
    },
    states: {
      isSaving,
    },
    commands: {
      createProvider,
    },
    methods: {
      // The field renders inside the consumer's <form>; without this, Enter
      // would implicitly submit that outer form instead of creating.
      handleNameKeyDown: (event: KeyboardEvent) => {
        if (event.key !== 'Enter') return

        event.preventDefault()
        createProvider()
      },
    },
  }
}
